import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckIcon, ShieldCheckIcon, TruckIcon } from 'lucide-react';
import type { Bundle, BundleId } from '../../data/pricing';
import { includedPerLight, includedPerLightEs, pricingConfig, remotesLabel, systemPurpose } from '../../data/pricing';
import { productAssets } from '../../data/images';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFunnel } from '../../contexts/FunnelContext';
import { useSystemSelection } from '../../contexts/SystemSelectionContext';
import { bundleName, formatMoney, getBundle, priceBundle, startingPrice } from '../../utils/pricing';
import { buttonClasses } from '../../utils/buttonStyles';
import { track } from '../../utils/analytics';
import { WarrantyTag } from '../ui/WarrantyTag';
import { ProductRating } from '../reviews/ProductRating';
type BundleSelectorProps = {
  /** External request (e.g. hero quick-pick) to select a specific system. */
  request?: {
    id: BundleId;
    n: number;
  } | null;
};

/** All seven M1 PRO systems — every value and image derives from data/pricing.ts. */
export function BundleSelector({
  request = null
}: BundleSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const es = lang === 'es';
  const {
    startSystem
  } = useFunnel();
  const {
    selectedId,
    setSelectedId
  } = useSystemSelection();
  useEffect(() => {
    if (request) setSelectedId(request.id);
  }, [request, setSelectedId]);
  const bundle = getBundle(selectedId);
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const included = es ? includedPerLightEs : includedPerLight;
  const packaging = productAssets.packaging;
  const summaryRef = useRef<HTMLDivElement>(null);
  const select = (b: Bundle) => {
    setSelectedId(b.id);
    track('bundle_view', {
      bundleId: b.id,
      content_ids: [b.variantId],
      qty: b.qty,
      value: priceBundle(b).total,
      currency: pricingConfig.currency
    });
  };

  /** On phones a tap scrolls down to the summary (image + price + what's in the box + Add to cart). */
  const tapOption = (e: React.MouseEvent) => {
    if (e.detail === 0) return; // keyboard-generated click — keep focus in the list
    if (!window.matchMedia('(max-width: 1023px)').matches) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Wait one frame so the summary already shows the newly selected system.
    requestAnimationFrame(() => summaryRef.current?.scrollIntoView({
      behavior: reduce ? 'auto' : 'smooth',
      block: 'start'
    }));
  };
  const ctaLabel = bundle.id === 'sys8' || bundle.id === 'sys24' ? `${es ? 'Quiero mi' : 'Get my'} ${es ? bundle.taglineEs : bundle.tagline} — ${m(p.total)}` : `${t('common.addToCart')} — ${m(p.total)}`;
  return <div className="grid gap-8 lg:grid-cols-[1fr_1.05fr] lg:gap-14" data-id="element-790" data-mp="2eo8g2wywds0a">
      {/* Preview — the approved artwork for the selected system. */}
      <div className="lg:sticky lg:top-28 lg:self-start" data-id="element-791" data-mp="2al3dyviqbsc6">
        <div className="mf-hud relative mx-auto aspect-[1122/1402] w-full max-w-[540px] overflow-hidden border border-marine/25 bg-[#06121F]" data-id="element-792" data-mp="q55atajfm2kw">
          {pricingConfig.bundles.map(b => <link key={b.id} rel="preload" as="image" href={b.image} data-id="element-793" data-mp="z4il25fdqv6o" />)}
          <AnimatePresence initial={false} data-id="element-794" data-mp="13vqzjfvkh6qp">
            <motion.img key={bundle.id} src={bundle.image} alt={`${bundleName(bundle, lang)} — ${bundle.qty} MarineForceLED M1 PRO light${bundle.qty === 1 ? '' : 's'}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="absolute inset-0 h-full w-full object-contain" data-id="element-795" data-mp="13wirmgpygmc8" />
          </AnimatePresence>
        </div>
        {packaging.src && packaging.ledCountVerified && <img src={packaging.src} alt="MarineForceLED M1 PRO retail packaging" loading="lazy" className="mt-4 hidden w-full border border-white/10 object-cover lg:block" data-id="element-796" data-mp="3ol6l9wfxi59h" />}
      </div>

      {/* Selection + purchase */}
      <div data-id="element-797" data-mp="33aiq8gpf2b3c">
        <ProductRating data-id="element-798" data-mp="33a8xwmgwfruh" />
        <h3 className="mt-3 font-display text-3xl font-semibold uppercase leading-none tracking-[0.03em] md:text-4xl" data-id="element-799" data-mp="339h51jeos87w">
          M1 PRO <span className="text-titanium" data-id="element-800" data-mp="2y91suv6556ri">— 13 LED RGB {es ? 'Luz recargable' : 'Rechargeable Light'}</span>
        </h3>
        <p className="mt-3 text-titanium" data-id="element-801" data-mp="25mh3g09874ql">
          {es ? 'Desde' : 'Starting at'} <span className="font-semibold text-white" data-id="element-802" data-mp="25ke1qiypnn61">{m(startingPrice())}</span>
          {es ? ' · cada sistema incluye el número indicado de luces M1 PRO y bases magnéticas.' : ' · every system includes the stated number of M1 PRO lights and magnetic bases.'}
        </p>

        <fieldset className="mt-6" data-id="element-803" data-mp="1rt9b26dbm57u">
          <legend className="sr-only" data-id="element-804" data-mp="1mvqze87x5bw5">{es ? 'Elige tu sistema' : 'Choose your system'}</legend>
          <div className="space-y-2" data-id="element-805" data-mp="1mtwkpbn8qg0i">
            {pricingConfig.bundles.map(b => <OptionRow key={b.id} bundle={b} checked={b.id === selectedId} onSelect={() => select(b)} onTap={tapOption} data-id="element-806" data-mp="2blwv5qsw1sh" />)}
          </div>
        </fieldset>

        <div ref={summaryRef} className="mt-6 scroll-mt-20 border border-white/10 bg-navy p-5 md:p-6" data-id="element-807" data-mp="3tkrlh2a4f3hg">
          {/* Phones/tablets: the selected system's image lives here, right above price and contents. */}
          <div className="relative mx-auto -mt-1 mb-5 aspect-[1122/1402] w-full max-w-[420px] overflow-hidden bg-[#06121F] lg:hidden" data-id="element-808" data-mp="q2rade99qhzm">
            <AnimatePresence initial={false} data-id="element-809" data-mp="g53bcc4mqobn">
              <motion.img key={bundle.id} src={bundle.image} alt={`${bundleName(bundle, lang)} — ${bundle.qty} MarineForceLED M1 PRO light${bundle.qty === 1 ? '' : 's'}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.2,
              ease: [0.23, 1, 0.32, 1]
            }} className="absolute inset-0 h-full w-full object-contain" data-id="element-810" data-mp="g3s8qfmx7mm4" />
            </AnimatePresence>
          </div>
          <div className="flex items-end justify-between gap-4" data-id="element-811" data-mp="xkfqrmb3z5sx">
            <div data-id="element-812" data-mp="sn5ze145sk2g">
              <p className="text-sm text-titanium" data-id="element-813" data-mp="slmjwcqbud1q">
                {bundleName(bundle, lang)} · {bundle.qty} M1 PRO
              </p>
              <p className="mt-1 font-display text-5xl font-semibold leading-none" data-id="element-814" data-mp="jmhnpd28mvsq">{m(p.total)}</p>
            </div>
            <div className="text-right text-sm" data-id="element-815" data-mp="en4sa3xawgej">
              <p className="text-titanium" data-id="element-816" data-mp="epr80quci27u">
                {m(p.perUnit)} {t('common.perLight')}
              </p>
              {p.savings > 0 && <p className="mt-0.5 font-semibold text-ice" data-id="element-817" data-mp="290w67fmnxmfs">
                  {es ? 'Ahorras' : 'You save'} {m(p.savings)}
                </p>}
            </div>
          </div>

          <button type="button" onClick={() => startSystem(bundle.id)} className={`${buttonClasses('primary', 'lg')} mt-5 w-full`} data-id="element-818" data-mp="2cko9cd7x4jym">
            {ctaLabel}
          </button>

          <div className="mt-5 grid gap-x-6 gap-y-2 border-t mf-hairline pt-5 text-sm text-titanium sm:grid-cols-2" data-id="element-819" data-mp="2lcgh2g5k5ezi">
            <div className="sm:col-span-2" data-id="element-820" data-mp="2qbja1289jur4">
              <WarrantyTag tier={bundle.warranty} linked withShipping data-id="element-821" data-mp="2qai236mjnodm" />
            </div>
            <p className="mt-1 font-display text-xs font-semibold uppercase tracking-[0.14em] text-white sm:col-span-2" data-id="element-822" data-mp="1tmlidsvt8cy6">
              {es ? 'En la caja' : 'In the box'}
            </p>
            {included.map(i => <p key={i} className="flex gap-2" data-id="element-823" data-mp="22o1rlxaw290t">
                <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-824" data-mp="27ltgeig8lf0s" />
                <span data-id="element-825" data-mp="3e1nefbt9zyj6">
                  {bundle.qty} × {i}
                </span>
              </p>)}
            <p className="flex gap-2 sm:col-span-2" data-id="element-826" data-mp="3iyvv17yrzuib">
              <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-marine" aria-hidden data-id="element-827" data-mp="7yled0s9trmu" />
              <span data-id="element-828" data-mp="7w8z4vdwdvdh">{remotesLabel(bundle.qty, lang)}</span>
            </p>
          </div>
        </div>
      </div>
    </div>;
}
type OptionRowProps = {
  bundle: Bundle;
  checked: boolean;
  onSelect: () => void;
  onTap: (e: React.MouseEvent) => void;
};
function OptionRow({
  bundle,
  checked,
  onSelect,
  onTap
}: OptionRowProps) {
  const {
    lang
  } = useLanguage();
  const es = lang === 'es';
  const p = priceBundle(bundle);
  const m = (n: number) => formatMoney(n, lang);
  const badge = bundle.badge === 'mostPopular' ? es ? 'Más popular' : 'Most popular' : bundle.badge === 'bestValue' ? es ? 'Mejor valor' : 'Best value' : null;
  const lifetime = bundle.warranty === 'lifetime';
  return <label onClick={onTap} className={`relative flex cursor-pointer gap-3 border p-2.5 pr-3 transition-[border-color,background-color] duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-ice sm:gap-4 ${checked ? 'border-marine bg-[#0D2440]' : bundle.featured ? 'border-marine/50 bg-[#081A2E] hover:border-marine' : 'border-white/15 bg-[#06121F] hover:border-marine/50'}`} data-id="element-829" data-mp="32enmpvirogxt">
      <input type="radio" name="m1pro-system" value={bundle.id} checked={checked} onChange={onSelect} className="sr-only" data-id="element-830" data-mp="144oq9wbadwdk" />
      <img src={bundle.image} alt="" loading="lazy" className="aspect-[1122/1402] w-[60px] shrink-0 self-center object-contain sm:w-[64px]" data-id="element-831" data-mp="184feptdcc0ex" />
      <span className="min-w-0 flex-1 self-center" data-id="element-832" data-mp="z6dh7nstfpwe">
        {/* Name and price share the first line so the price is always visible, at any width. */}
        <span className="flex items-baseline justify-between gap-2" data-id="element-833" data-mp="l8f8axx9kloa">
          <span className="min-w-0 font-display text-lg font-semibold uppercase leading-tight tracking-[0.03em] text-white" data-id="element-834" data-mp="p8pby3xl2zzz">
            {es ? bundle.taglineEs : bundle.tagline}
          </span>
          <span className="shrink-0 whitespace-nowrap font-display text-xl font-semibold leading-tight text-white" data-id="element-835" data-mp="2jknly83pvzwx">{m(p.total)}</span>
        </span>
        {badge && <span className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-ice" data-id="element-836" data-mp="36xunfu4m40eu">{badge}</span>}
        <span className="block text-[13px] text-titanium" data-id="element-837" data-mp="2s9qdpfnjez87">
          {bundle.qty} M1 PRO · {m(p.perUnit)} {es ? 'por luz' : 'per light'}
          {p.savings > 0 && <>
              {' · '}
              <span className="text-ice" data-id="element-838" data-mp="3kxm6wadwe908">
                {es ? 'Ahorra' : 'Save'} {m(p.savings)}
              </span>
            </>}
        </span>
        <span className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]" data-id="element-839" data-mp="3pumfary7znui">
          <span className={`inline-flex items-center gap-1 ${lifetime ? 'text-white' : 'text-titanium'}`} data-id="element-840" data-mp="3bza7pjyyu2w6">
            <ShieldCheckIcon className={`h-3.5 w-3.5 ${lifetime ? 'text-marine' : 'text-titanium'}`} aria-hidden data-id="element-841" data-mp="3g0nhg556iq8d" />
            {lifetime ? es ? 'Garantía de por vida' : 'Lifetime warranty' : es ? 'Garantía de 1 año' : '1-year warranty'}
          </span>
          <span className="inline-flex items-center gap-1 text-white" data-id="element-842" data-mp="1wytkfog6spqd">
            <TruckIcon className="h-3.5 w-3.5 text-marine" aria-hidden data-id="element-843" data-mp="1s1jnlnqgxbsi" />
            <span className="sm:hidden" data-id="element-844" data-mp="1s3ebnax1dk0t">{es ? 'Envío gratis' : 'Free shipping'}</span>
            <span className="hidden sm:inline" data-id="element-845" data-mp="26w7yjhen12fi">{es ? 'Envío gratis en EE.UU. · Rastreado y asegurado' : 'Free U.S. Shipping · Tracked & Insured'}</span>
          </span>
        </span>
        {checked && <span className="mt-1.5 block text-[13px] leading-snug text-titanium" data-id="element-846" data-mp="1e7ivvdxxop1n">{systemPurpose[bundle.id][es ? 'es' : 'en']}</span>}
      </span>
    </label>;
}